import React, { useState, useEffect } from 'react';
import moment from 'moment';
import axiosInstance from '../utills/axiosInstance';
import CheckinCheckout from './CheckinCheckout';
import Timeline from './Timeline';
import ODModal from './ODModal';
import { useAttendance } from './useAttendance';
import './Attendance.css';
import { Button, Dialog, DialogTitle, DialogContent, DialogActions, IconButton, Box, Typography } from '@mui/material';
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';
import CloseIcon from '@mui/icons-material/Close';
import { StaticDatePicker } from '@mui/x-date-pickers/StaticDatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';

const Attendance = () => {
    const [weekData, setWeekData] = useState([]);
    const [currentWeek, setCurrentWeek] = useState({
        year: moment().isoWeekYear(),
        weekNum: moment().isoWeek()
    });
    const [openODCalendar, setOpenODCalendar] = useState(false);
    const [selectedODDate, setSelectedODDate] = useState(null);
    const [openODModal, setOpenODModal] = useState(false);
    const [blockedDates, setBlockedDates] = useState([]);

    const profile = JSON.parse(localStorage.getItem('profile') || '{}');
    const empId = profile.empId || '';
    const userId = profile._id || '';
    const attendance = useAttendance(empId);
    const isAdmin = profile.employeeRole === 'Admin';

    const fetchBlockedDates = async () => {
        if (!userId) return;
        try {
            const res = await axiosInstance.get(`/api/blocked-dates/${userId}`);
            setBlockedDates(res.data.blockedDates || []);
        } catch (err) {
            console.error('Failed to fetch blocked dates', err);
            setBlockedDates([]);
        }
    };

    useEffect(() => {
        fetchBlockedDates();
    }, [userId]);

    const isDateBlocked = (date) => {
        if (!date) return true;
        const dateStr = moment(date).format('YYYY-MM-DD');
        const isSunday = moment(date).day() === 0;
        return blockedDates.includes(dateStr) || isSunday;
    };

    const fetchWeeklyData = async () => {
        if (!empId) return;
        const targetEmpId = empId;
        try {
            const res = await axiosInstance.get(
                `/api/week/${targetEmpId}/${currentWeek.year}/${currentWeek.weekNum}`
            );
            let data = res.data.weekData || [];
            if (data.length > 0) {
                const startDate = moment(data[0].date).startOf('day').toISOString();
                const endDate = moment(data[data.length - 1].date).endOf('day').toISOString();
                try {
                    const pendingRes = await axiosInstance.get('/api/od/pending', {
                        params: { empId: targetEmpId, from: startDate, to: endDate }
                    });
                    const pendingDates = new Set(
                        pendingRes.data
                        .filter(od => od.status === 'Pending')
                        .map(od => moment(od.date).format('YYYY-MM-DD'))
                    );
                    data = data.map(day => {
                        const dayKey = moment(day.date).format('YYYY-MM-DD');
                        if (pendingDates.has(dayKey)) {
                            return { ...day, status: 'Requested', leaveType: 'OD' };
                        }
                        return day;
                    });
                } catch (e) {
                    console.warn('Could not fetch pending ODs', e);
                }
            }
            setWeekData(data);
        } catch (err) {
            console.error('Failed to load attendance', err);
        }
    };

    useEffect(() => {
        fetchWeeklyData();
    }, [currentWeek.year, currentWeek.weekNum, empId]);

    const handleApplyOD = () => {
        setOpenODCalendar(true);
    };

    const handleDateSelect = (date) => {
        if (isDateBlocked(date)) {
            alert('Cannot apply OD: Date is already used or a Sunday.');
            return;
        }
        setSelectedODDate(date);
        setOpenODCalendar(false);
        setOpenODModal(true);
    };

    const handleODSubmit = (success) => {
        setOpenODModal(false);
        setSelectedODDate(null);
        if (success) {
            fetchWeeklyData();
            fetchBlockedDates();
        }
    };

    const formatHoursToHM = (hours) => {
        if (!hours || hours === 0) return '00:00';
        const h = Math.floor(hours);
        const m = Math.round((hours % 1) * 60);
        return `${h.toString().padStart(2, '0')}:${m.toString().padStart(2, '0')}`;
    };

    const prevWeek = () => {
        let week = currentWeek.weekNum - 1;
        let year = currentWeek.year;
        if (week < 1) {
            year -= 1;
            week = moment().year(year).isoWeeksInYear();
        }
        setCurrentWeek({ year, weekNum: week });
    };

    const nextWeek = () => {
        const isCurrentWeek = moment().isoWeekYear() === currentWeek.year && moment().isoWeek() === currentWeek.weekNum;
        if (isCurrentWeek) return;
        let week = currentWeek.weekNum + 1;
        let year = currentWeek.year;
        if (week > moment().year(year).isoWeeksInYear()) {
            week = 1;
            year += 1;
        }
        setCurrentWeek({ year, weekNum: week });
    };

    return (
        <LocalizationProvider dateAdapter={AdapterDateFns}>
            <div className="attendance-container">
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
                    <Typography variant="h5">
                        Attendance
                    </Typography>

                    <Box sx={{ display: 'flex', gap: 2, alignItems: 'center'}}>
                        <Button
                            variant="contained"
                            color="secondary"
                            size="small"
                            startIcon={<CalendarTodayIcon />}
                            onClick={handleApplyOD}
                        >
                            Apply OD
                        </Button>

                        <CheckinCheckout
                            showTimer={attendance.showTimer}
                            elapsedTime={attendance.elapsedTime}
                            isBreak={attendance.isBreak}
                            formatTime={attendance.formatTime}
                            handleCheckin={attendance.handleCheckin}
                            handleToggleBreak={attendance.handleToggleBreak}
                            handleCheckout={attendance.handleCheckout}
                            canCheckin={attendance.canCheckin}
                            hasTodayCheckout={attendance.hasTodayCheckout}
                        />
                    </Box>
                </Box>

                <div className="week-navigation">
                    <button onClick={prevWeek}>← Prev Week</button>
                    <span className="week-label">
                        {moment().year(currentWeek.year).isoWeek(currentWeek.weekNum).format('MMM DD')} -{' '}
                        {moment().year(currentWeek.year).isoWeek(currentWeek.weekNum).add(5, 'days').format('MMM DD, YYYY')}
                    </span>
                    <button
                        onClick={nextWeek}
                        disabled={moment().isoWeekYear() === currentWeek.year && moment().isoWeek() === currentWeek.weekNum}
                    >
                        Next Week →
                    </button>
                </div>

                <table className="attendance-table">
                    <thead>
                        <tr>
                            <th>Day</th>
                            <th>Timeline</th>
                        </tr>
                    </thead>
                    <tbody>
                        {weekData.map((row, i) => (
                            <tr key={i}>
                                <td>{moment(row.date).format('MMM DD, ddd')}</td>
                                <td>
                                    <Timeline
                                        checkin={row.checkin}
                                        checkout={row.checkout}
                                        breaks={row.breaks || []}
                                        status={row.status}
                                        leaveType={row.leaveType}
                                        totalHours={row.totalHours}
                                        formatHoursToHM={formatHoursToHM}
                                    />
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>

                <Dialog open={openODCalendar} onClose={() => setOpenODCalendar(false)} maxWidth="sm" fullWidth>
                    <DialogTitle>
                        Select Date for On Duty
                        <IconButton
                            onClick={() => setOpenODCalendar(false)}
                            sx={{ position: 'absolute', right: 8, top: 8 }}
                        >
                            <CloseIcon />
                        </IconButton>
                    </DialogTitle>
                    <DialogContent dividers>
                        <StaticDatePicker
                            displayStaticWrapperAs="desktop"
                            value={selectedODDate}
                            onChange={handleDateSelect}
                            shouldDisableDate={isDateBlocked}
                            minDate={new Date('2020-01-01')}
                        />
                    </DialogContent>
                    <DialogActions>
                        <Button onClick={() => setOpenODCalendar(false)}>Cancel</Button>
                    </DialogActions>
                </Dialog>

                <ODModal
                    open={openODModal}
                    day={selectedODDate}
                    onClose={() => {
                        setOpenODModal(false);
                        setSelectedODDate(null);
                    }}
                    onSubmit={handleODSubmit}
                />
            </div>
        </LocalizationProvider>
    );
};

export default Attendance;
